import { useState, useRef, useEffect, useCallback } from 'react'
import DOMPurify from 'dompurify'
import {
    MdReply,
    MdReplyAll,
    MdForward,
    MdExpandMore,
    MdExpandLess,
    MdAttachFile,
    MdDownload,
} from 'react-icons/md'
import type {
    EmailMessageDetail,
    EmailAccount,
} from '@/lib/api/administrative/inbox-api'
import { getAttachmentDownloadUrl } from '@/lib/api/administrative/inbox-api'

interface ThreadViewProps {
    messages: EmailMessageDetail[]
    currentAccount: EmailAccount
    onReply: (msg: EmailMessageDetail) => void
    onReplyAll: (msg: EmailMessageDetail) => void
    onForward: (msg: EmailMessageDetail) => void
    canCreate?: boolean
}

function formatFullDate(dateStr: string): string {
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const ALLOWED_TAGS = [
    'a', 'b', 'br', 'blockquote', 'code', 'div', 'em', 'h1', 'h2',
    'h3', 'h4', 'h5', 'h6', 'hr', 'i', 'img', 'li', 'ol', 'p',
    'pre', 'span', 'strong', 'table', 'tbody', 'td', 'th', 'thead',
    'tr', 'u', 'ul', 'font', 'center', 'sub', 'sup', 'small',
]

const ALLOWED_ATTRS = [
    'href', 'src', 'alt', 'title', 'style', 'class', 'width',
    'height', 'target', 'rel', 'cellpadding', 'cellspacing',
    'border', 'align', 'valign', 'bgcolor', 'color', 'size', 'face',
]

function sanitizeHtml(html: string): string {
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS,
        ALLOWED_ATTR: ALLOWED_ATTRS,
    })
}

function MessageIframe({ html }: { html: string }) {
    const iframeRef = useRef<HTMLIFrameElement>(null)
    const blobUrlRef = useRef<string | null>(null)

    const adjustHeight = useCallback(() => {
        const iframe = iframeRef.current
        if (!iframe?.contentDocument?.body) return
        const height = iframe.contentDocument.body.scrollHeight
        iframe.style.height = `${Math.max(height + 32, 100)}px`
    }, [])

    useEffect(() => {
        const sanitized = sanitizeHtml(html)
        const fullHtml = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <meta name="referrer" content="no-referrer" />
    <base target="_blank" rel="noopener noreferrer" />
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            line-height: 1.5;
            color: #333;
            margin: 0;
            padding: 12px;
            word-wrap: break-word;
        }
        img { max-width: 100%; height: auto; }
        a { color: #1a73e8; }
        blockquote {
            border-left: 3px solid #ddd;
            padding-left: 12px;
            margin-left: 0;
            color: #555;
        }
        table { border-collapse: collapse; max-width: 100%; }
        td, th { padding: 4px 8px; }
    </style>
</head>
<body>${sanitized}</body>
</html>`

        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current)
        }

        const blob = new Blob([fullHtml], { type: 'text/html' })
        const url = URL.createObjectURL(blob)
        blobUrlRef.current = url

        const iframe = iframeRef.current
        if (iframe) {
            iframe.src = url
        }

        return () => {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current)
                blobUrlRef.current = null
            }
        }
    }, [html])

    useEffect(() => {
        const iframe = iframeRef.current
        if (!iframe) return
        iframe.addEventListener('load', adjustHeight)
        return () => iframe.removeEventListener('load', adjustHeight)
    }, [adjustHeight])

    return (
        <iframe
            ref={iframeRef}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            className="w-full border-0"
            style={{ minHeight: 100 }}
            title="Conteúdo do email"
        />
    )
}

function ThreadMessage({
    message,
    isLast,
    onReply,
    onReplyAll,
    onForward,
    canCreate,
}: {
    message: EmailMessageDetail
    isLast: boolean
    onReply: (msg: EmailMessageDetail) => void
    onReplyAll: (msg: EmailMessageDetail) => void
    onForward: (msg: EmailMessageDetail) => void
    canCreate?: boolean
}) {
    const [expanded, setExpanded] = useState(isLast)

    const hasMultipleRecipients =
        (message.toAddresses?.split(',').length || 0) > 1 ||
        !!message.ccAddresses

    return (
        <div className="border border-base-200 rounded-lg overflow-hidden">
            {/* Header - clicavel para expandir */}
            <button
                onClick={() => setExpanded(!expanded)}
                className={`w-full flex items-start gap-3 p-3 text-left hover:bg-base-100 transition-colors ${
                    expanded ? 'bg-base-100 border-b border-base-200' : ''
                }`}
            >
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                    {(
                        message.fromName ||
                        message.fromAddress ||
                        '?'
                    )[0].toUpperCase()}
                </div>

                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                            <span className="font-semibold text-sm truncate block">
                                {message.fromName || message.fromAddress}
                            </span>
                            {message.fromName && (
                                <span className="text-xs text-base-content/50 truncate block">
                                    {message.fromAddress}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-base-content/50">
                                {message.receivedAt
                                    ? formatFullDate(message.receivedAt)
                                    : ''}
                            </span>
                            {expanded ? (
                                <MdExpandLess className="text-base" />
                            ) : (
                                <MdExpandMore className="text-base" />
                            )}
                        </div>
                    </div>

                    {!expanded && message.snippet && (
                        <p className="text-xs text-base-content/50 truncate mt-0.5">
                            {message.snippet}
                        </p>
                    )}
                </div>
            </button>

            {/* Corpo expandido */}
            {expanded && (
                <div className="p-3">
                    {/* Destinatarios */}
                    <div className="text-xs text-base-content/50 mb-3 space-y-0.5">
                        {message.toAddresses && (
                            <p>
                                <span className="font-semibold">Para:</span>{' '}
                                {message.toAddresses}
                            </p>
                        )}
                        {message.ccAddresses && (
                            <p>
                                <span className="font-semibold">Cc:</span>{' '}
                                {message.ccAddresses}
                            </p>
                        )}
                    </div>

                    {/* Corpo do email */}
                    {message.bodyHtml ? (
                        <MessageIframe html={message.bodyHtml} />
                    ) : message.bodyText ? (
                        <pre className="whitespace-pre-wrap text-sm font-sans p-3 bg-base-100 rounded-lg">
                            {message.bodyText}
                        </pre>
                    ) : (
                        <p className="text-sm text-base-content/40 italic">
                            Sem conteúdo
                        </p>
                    )}

                    {/* Anexos */}
                    {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-3 border-t border-base-200 pt-3">
                            <p className="text-xs font-semibold text-base-content/50 mb-2 flex items-center gap-1">
                                <MdAttachFile className="text-sm" />
                                {message.attachments.length} anexo
                                {message.attachments.length > 1 ? 's' : ''}
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {message.attachments.map(att => (
                                    <a
                                        key={att.id}
                                        href={getAttachmentDownloadUrl(
                                            message.id,
                                            att.id,
                                        )}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1.5 bg-base-200 rounded-lg px-3 py-1.5 text-xs hover:bg-base-300 transition-colors"
                                    >
                                        <MdDownload className="text-sm" />
                                        <span className="truncate max-w-[150px]">
                                            {att.filename || 'Anexo'}
                                        </span>
                                        {att.size && (
                                            <span className="text-base-content/40">
                                                ({formatFileSize(att.size)})
                                            </span>
                                        )}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Acoes de resposta */}
                    {canCreate && (
                        <div className="mt-3 border-t border-base-200 pt-3 flex items-center gap-1">
                            <button
                                onClick={() => onReply(message)}
                                className="btn btn-ghost btn-xs gap-1"
                            >
                                <MdReply className="text-base" />
                                Responder
                            </button>
                            {hasMultipleRecipients && (
                                <button
                                    onClick={() => onReplyAll(message)}
                                    className="btn btn-ghost btn-xs gap-1"
                                >
                                    <MdReplyAll className="text-base" />
                                    Responder a todos
                                </button>
                            )}
                            <button
                                onClick={() => onForward(message)}
                                className="btn btn-ghost btn-xs gap-1"
                            >
                                <MdForward className="text-base" />
                                Encaminhar
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default function ThreadView({
    messages,
    currentAccount,
    onReply,
    onReplyAll,
    onForward,
    canCreate,
}: ThreadViewProps) {
    if (messages.length === 0) {
        return (
            <div className="flex items-center justify-center h-40 text-base-content/40">
                Nenhuma mensagem na conversa
            </div>
        )
    }

    const sorted = [...messages].sort(
        (a, b) =>
            new Date(a.receivedAt || 0).getTime() -
            new Date(b.receivedAt || 0).getTime(),
    )

    return (
        <div className="flex flex-col gap-2">
            {/* Assunto da thread */}
            <h2 className="text-lg font-semibold px-1">
                {sorted[0].subject || '(Sem assunto)'}
            </h2>
            <p className="text-xs text-base-content/50 px-1">
                {sorted.length} mensage{sorted.length > 1 ? 'ns' : 'm'} na
                conversa
            </p>

            {/* Lista de mensagens */}
            <div className="flex flex-col gap-2 mt-2">
                {sorted.map((msg, idx) => (
                    <ThreadMessage
                        key={msg.id}
                        message={msg}
                        isLast={idx === sorted.length - 1}
                        onReply={onReply}
                        onReplyAll={onReplyAll}
                        onForward={onForward}
                        canCreate={canCreate}
                    />
                ))}
            </div>
        </div>
    )
}
